/**
 * @fileoverview Hot Actions Store (Zustand)
 * 
 * Manages hot actions state: notifications, requests, WebSocket events.
 * Replaces HotActionsContext with better performance.
 * 
 * @module stores/useHotActionsStore
 */

import { create } from 'zustand';
import { 
  getHotActionNotifications, 
  updateNotificationStatus, 
  markAllNotificationsAsCompleted 
} from '@/utils/hotActions';
import { wsService } from '@/services/websocket';
import { useAuthStore } from './useAuthStore';

/**
 * Transform backend notification to local format
 */
const transformNotification = (notification) => ({
  id: notification.id,
  message: notification.message,
  tableId: notification.table_id,
  tableName: notification.table_name || `Table ${notification.table_id}`,
  action: notification.action,
  notes: notification.notes || '',
  timestamp: notification.created_at,
  status: notification.status?.toLowerCase() || 'pending'
});

/**
 * Hot Actions Store
 * 
 * @example
 * // Use specific selectors for better performance
 * const hotActionRequests = useHotActionsStore((state) => state.hotActionRequests);
 * const newRequestsCount = useHotActionsStore((state) => state.newRequestsCount);
 * 
 * // Or use the legacy hook for backward compatibility
 * const { hotActionRequests, completeRequest, dismissRequest } = useHotActions();
 */
export const useHotActionsStore = create((set, get) => ({
  // State
  hotActionRequests: [],
  newRequestsCount: 0,
  isLoading: false,
  _restaurantId: null,
  _wsCleanup: null,

  // Internal: Get user data for API calls
  _getUser: () => {
    if (typeof window !== 'undefined') {
      return JSON.parse(localStorage.getItem('userObj') || '{}');
    }
    return {};
  },

  // Fetch notifications from backend
  fetchNotifications: async (restaurantId) => {
    const rid = restaurantId || get()._restaurantId;
    if (!rid) return;
    
    set({ isLoading: true });
    try {
      const response = await getHotActionNotifications(rid);
      const notifications = response.notifications || [];
      
      const transformedNotifications = notifications.map(transformNotification);
      
      const pendingCount = transformedNotifications.filter(n => n.status === 'pending').length;
      
      set({
        hotActionRequests: transformedNotifications,
        newRequestsCount: pendingCount,
        isLoading: false
      });
    } catch (error) {
      console.error('Error fetching notifications:', error);
      set({ isLoading: false });
    }
  },

  // Add new hot action request (from WebSocket).
  // The backend has ALREADY stored the request (the event carries its id): never
  // create it again here — the old POST fallback wrote a duplicate row every time
  // the list fetch missed it (or failed). Read the stored row when possible, else
  // insert it locally from the event payload; always dedupe by id.
  addHotActionRequest: async (requestData) => {
    const { _restaurantId } = get();

    if (!_restaurantId) {
      console.error('No restaurant ID available for fetching notification');
      return;
    }

    const insert = (request) => {
      set(state => {
        if (state.hotActionRequests.some(n => String(n.id) === String(request.id))) return state;
        return {
          hotActionRequests: [request, ...state.hotActionRequests],
          newRequestsCount: state.newRequestsCount + 1
        };
      });
    };

    const notificationId = requestData.notificationId ?? requestData.notification_id ?? null;

    if (notificationId != null) {
      try {
        const response = await getHotActionNotifications(_restaurantId);
        const existingNotification = (response?.notifications || []).find(
          n => String(n.id) === String(notificationId)
        );
        if (existingNotification) {
          insert(transformNotification(existingNotification));
          return;
        }
      } catch (error) {
        console.error('Error fetching existing notification:', error);
      }
    }

    // Local copy built from the event (no backend write).
    const tableId = requestData.tableId || requestData.table_id || null;
    insert({
      id: notificationId != null ? Number(notificationId) || notificationId : `temp_${Date.now()}`,
      message: requestData.message || 'Service request',
      tableId,
      tableName: requestData.tableName || requestData.table_name || `Table ${tableId || '?'}`,
      action: requestData.action || (requestData.message && requestData.message.replace('Service request: ', '')) || 'unknown',
      notes: requestData.notes || '',
      timestamp: new Date(),
      status: 'pending'
    });
  },

  // Mark request as completed with backend sync
  completeRequest: async (requestId) => {
    const { _restaurantId } = get();
    
    try {
      if (_restaurantId && requestId) {
        await updateNotificationStatus(_restaurantId, requestId, 'COMPLETED');
      }
      
      set(state => ({
        hotActionRequests: state.hotActionRequests.map(request => 
          request.id === requestId 
            ? { ...request, status: 'completed' }
            : request
        ),
        newRequestsCount: Math.max(0, state.newRequestsCount - 1)
      }));
    } catch (error) {
      console.error('Error completing request:', error);
    }
  },

  // Mark request as dismissed with backend sync
  dismissRequest: async (requestId) => {
    const { _restaurantId } = get();
    
    try {
      if (_restaurantId && requestId) {
        await updateNotificationStatus(_restaurantId, requestId, 'DISMISSED');
      }
      
      set(state => ({
        hotActionRequests: state.hotActionRequests.map(request => 
          request.id === requestId 
            ? { ...request, status: 'dismissed' }
            : request
        ),
        newRequestsCount: Math.max(0, state.newRequestsCount - 1)
      }));
    } catch (error) {
      console.error('Error dismissing request:', error);
    }
  },

  // Clear all requests with backend sync
  clearAllRequests: async () => {
    const { _restaurantId } = get();
    
    try {
      if (_restaurantId) {
        await markAllNotificationsAsCompleted(_restaurantId);
      }
      
      set(state => ({
        hotActionRequests: state.hotActionRequests.map(request => ({ ...request, status: 'completed' })),
        newRequestsCount: 0
      }));
    } catch (error) {
      console.error('Error clearing all requests:', error);
    }
  },

  // Mark all as seen (reset counter without backend sync)
  markAllAsSeen: () => {
    set({ newRequestsCount: 0 });
  },

  // Refresh notifications from backend
  refreshNotifications: () => {
    get().fetchNotifications();
  },

  // Initialize store with restaurant ID and setup WebSocket
  initialize: (restaurantId) => {
    if (!restaurantId) {
      console.warn("No restaurant ID found for hot actions");
      return;
    }

    const { _wsCleanup } = get();
    
    // Cleanup previous WebSocket listeners if any
    if (_wsCleanup) {
      _wsCleanup();
    }

    set({ _restaurantId: restaurantId });

    // Fetch initial notifications from backend
    get().fetchNotifications(restaurantId);

    // Listen for table::available events which contain hot action data
    const handleTableAvailable = (data) => {
      
      if (typeof data?.message === 'string' && data.message.includes('Service request:')) {
        get().addHotActionRequest(data).catch(console.error);
      }
    };

 

    // Join the restaurant's room; refetch when events may have been missed.
    const room = `admin-restaurant:${restaurantId}`;
    const release = wsService.join(room, { onResync: () => get().fetchNotifications(restaurantId) });
    const off = wsService.on('table::available', handleTableAvailable, { room });

    // Store cleanup function (also leaves the room on a restaurant switch)
    const cleanup = () => {
      off();
      release();
    };

    set({ _wsCleanup: cleanup });

    return cleanup;
  },

  // Cleanup WebSocket listeners
  cleanup: () => {
    const { _wsCleanup } = get();
    if (_wsCleanup) {
      _wsCleanup();
      set({ _wsCleanup: null });
    }
  },

  // Reset store state
  reset: () => {
    const { cleanup } = get();
    cleanup();
    set({
      hotActionRequests: [],
      newRequestsCount: 0,
      isLoading: false,
      _restaurantId: null,
      _wsCleanup: null
    });
  }
}));

// Helper to check if user has valid auth token
const hasValidAuthToken = () => {
  if (typeof window === 'undefined') return false;
  const userObj = JSON.parse(localStorage.getItem('userObj') || 'null');
  return !!(userObj?.access_token);
};

// Auto-initialize when restaurant becomes available (module level subscription)
if (typeof window !== 'undefined') {
  let prevRestaurantId = null;
  
  useAuthStore.subscribe((state) => {
    const restaurantId = state.restaurant?.id;
    const store = useHotActionsStore.getState();
    
    // Initialize when restaurant becomes available or changes
    // Only if user is actually authenticated (has valid token)
    if (restaurantId && restaurantId !== store._restaurantId && hasValidAuthToken()) {
      store.initialize(restaurantId);
    }
    
    // Cleanup when restaurant is cleared (logout)
    if (!restaurantId && prevRestaurantId) {
      store.reset();
    }
    
    prevRestaurantId = restaurantId;
  });
  
  // Also check immediately on load - but only if authenticated
  setTimeout(() => {
    const authState = useAuthStore.getState();
    const hotActionsState = useHotActionsStore.getState();
    
    if (authState.restaurant?.id && !hotActionsState._restaurantId && hasValidAuthToken()) {
      hotActionsState.initialize(authState.restaurant.id);
    }
  }, 100);
}

/**
 * Legacy hook for backward compatibility
 * Use this hook to get the same API as the old HotActionsContext
 * 
 * @example
 * const { hotActionRequests, completeRequest, dismissRequest } = useHotActions();
 */
export const useHotActions = () => {
  const hotActionRequests = useHotActionsStore((state) => state.hotActionRequests);
  const newRequestsCount = useHotActionsStore((state) => state.newRequestsCount);
  const isLoading = useHotActionsStore((state) => state.isLoading);
  const addHotActionRequest = useHotActionsStore((state) => state.addHotActionRequest);
  const completeRequest = useHotActionsStore((state) => state.completeRequest);
  const dismissRequest = useHotActionsStore((state) => state.dismissRequest);
  const clearAllRequests = useHotActionsStore((state) => state.clearAllRequests);
  const markAllAsSeen = useHotActionsStore((state) => state.markAllAsSeen);
  const refreshNotifications = useHotActionsStore((state) => state.refreshNotifications);

  return {
    hotActionRequests,
    newRequestsCount,
    isLoading,
    addHotActionRequest,
    completeRequest,
    dismissRequest,
    clearAllRequests,
    markAllAsSeen,
    refreshNotifications
  };
};
